"use client";

import { useCallback, useMemo } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

function parseUrl(url: string): { isDifferentHost: boolean; url: string } {
  if (url.startsWith("/")) {
    return { isDifferentHost: false, url };
  }
  try {
    const { hostname: target, pathname, search, hash } = new URL(url);
    const { hostname: current } = window.location;
    if (target !== current) {
      return { isDifferentHost: true, url };
    } else {
      return { isDifferentHost: false, url: [pathname, search, hash].join("") };
    }
  } catch {
    return { isDifferentHost: false, url };
  }
}

export default function useNavigation() {
  const navigate = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const updateUrl = useCallback(
    (params: Record<string, string | number | boolean>) => {
      const newParams = new URLSearchParams(searchParams.toString());
      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });
      return [pathname, newParams.toString()].filter(Boolean).join("?");
    },
    [searchParams, pathname]
  );
  const back = useCallback(() => {
    navigate.back();
  }, [navigate]);

  const replace = useCallback(
    (_url: string | { searchParams?: Record<string, string | number | boolean> }) => {
      if (typeof _url === "object") {
        return navigate.replace(updateUrl(_url.searchParams), { scroll: false });
      }
      const { isDifferentHost, url } = parseUrl(_url);
      if (isDifferentHost) {
        window.location.replace(url);
      } else {
        navigate.replace(url, { scroll: false });
      }
    },
    [navigate, updateUrl]
  );
  const push = useCallback(
    (_url: string | { searchParams?: Record<string, string | number | boolean> }) => {
      if (typeof _url === "object") {
        return navigate.push(updateUrl(_url.searchParams), { scroll: false });
      }

      const { isDifferentHost, url } = parseUrl(_url);
      if (isDifferentHost) {
        window.location.assign(url);
      } else {
        navigate.push(url, { scroll: false });
      }
    },
    [navigate, updateUrl]
  );

  return useMemo(() => ({ replace, push, back }), [replace, push, back]);
}
